// Catch-all page rendered by React Router's `*` route when no other
// route matches. Three audiences land here, and each needs a different
// recovery surface:
//
//   - admin           → `/admin/dashboard` (their home)
//   - field_staff     → `/capture`         (their home; admin
//                                            dashboard is gated by
//                                            RequireAdmin and would
//                                            bounce them straight off)
//   - unauthenticated → `/sign-in`         (no home yet — start with
//                                            sign-in so the user can
//                                            pick up wherever they
//                                            were heading)
//
// Picking the role-appropriate destination keeps a stray-link / typo
// recoverable for everyone. The previous version hardcoded
// `/admin/dashboard` and silently broke field_staff who would tap "Go
// to dashboard" and immediately get redirected by RequireAdmin —
// dead-end UX.

import { Link } from 'react-router'

import { useAuth } from './auth-provider'

interface RecoveryTarget {
  to: string
  label: string
}

export function recoveryTargetForRole(
  role: 'admin' | 'field_staff' | undefined,
): RecoveryTarget {
  if (role === 'admin') {
    return { to: '/admin/dashboard', label: 'Go to dashboard' }
  }
  if (role === 'field_staff') {
    return { to: '/capture', label: 'Go to capture' }
  }
  // Unauthenticated, loading, or error — sign-in is the only safe
  // landing. Loading is rare here (the catch-all fires synchronously
  // on render); funneling through /sign-in still works because that
  // route preserves redirect_to from the URL.
  return { to: '/sign-in', label: 'Sign in' }
}

export function NotFoundPage() {
  const { user } = useAuth()
  const target = recoveryTargetForRole(user?.role)
  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-md space-y-3 text-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          Page not found
        </h1>
        <p className="text-sm text-gray-600">
          The page you’re looking for doesn’t exist or has been moved.
        </p>
        <Link
          to={target.to}
          className="inline-block text-sm text-[color:var(--color-brand)] underline"
        >
          {target.label}
        </Link>
      </div>
    </main>
  )
}
